import React, { createContext, ReactNode, useContext, useState } from 'react';
import { Shopify, ProductMetafields } from './types';
import { OnlineProduct } from '@jbhi-fi/apis/src/shopify/types/OnlineProduct';
import { ShopifyProduct } from '@jbhi-fi/components/src/Shopify/types';
import { STORE_LOCATIONS } from '../../utils/location/locationHelper';
import {
  getProductMessages,
  PRODUCT_MESSAGE_TYPES,
} from '@jbhi-fi/components/src/ProductDetail/ProductMessages/utils/getProductMessages';
import { getSpecialOffersFromMessages } from '@jbhi-fi/components/src/ProductDetail/SpecialOffers/utils/getSpecialOffersFromMessages';
import { toCamelCase } from '@jbhi-fi/utils/src/toCamelCase';
import { Message } from '@jbhi-fi/components/src/PdpProductProvider/types';
import { EX_DISPLAY_SUBTYPE } from '@jbhi-fi/components/src/ProductDetail/SpecialOffers/constants';

const ProductContext = createContext<Shopify>(null);

export const useShopifyProduct = () => {
  const context = useContext(ProductContext);
  if (context === undefined || context === null) {
    throw new Error('useShopifyProduct must be used within a ShopifyProductProvider');
  }
  return context;
};

export const ShopifyProductProvider = ({ children }: { children: ReactNode }) => {
  const [shopify, setShopify] = useState<ShopifyProduct>(window.Jbhifi['product']);
  const [metafields, setMetafields] = useState<ProductMetafields>(window.Jbhifi['product.metafields']);

  const productMetafields = metafields?.online_product?.value ?? ({} as OnlineProduct);
  const allMessages: Array<Message> = toCamelCase(productMetafields.Display?.Messages ?? []);
  const installationMessages = [];
  const productFlags = productMetafields.Product?.Flags;
  const isHideAwaitingStock: boolean = productFlags?.some(flag => flag.Name === 'Hide Awaiting Stock');
  const isAwaitingStock: boolean = productMetafields.Availability?.AvailabilityStatement === 'Awaiting stock';
  const isPreOrder: boolean = productMetafields.Availability?.AvailabilityStatement === 'Pre-order online';

  const storeTimeZone =
    window.Jbhifi?.storeLocation === STORE_LOCATIONS.NZ ? 'Pacific/Auckland' : 'Australia/Melbourne';
  const specialOffers = getSpecialOffersFromMessages(allMessages, storeTimeZone);
  const hasExDisplayMessage = specialOffers?.some(offer => offer.subType === EX_DISPLAY_SUBTYPE) ?? false;
  const productMessages = getProductMessages(allMessages, isHideAwaitingStock, isAwaitingStock, isPreOrder);
  if (allMessages.length > 0) {
    const installMessage = allMessages.find(message => message.subType === PRODUCT_MESSAGE_TYPES.INSTALLATION);
    if (installMessage) installationMessages.push(installMessage);
  }

  return (
    <ProductContext.Provider
      value={{
        shopify,
        setShopify,
        metafields,
        setMetafields,
        productMetafields,
        marketplaceSellers: metafields?.marketplaceSellers,
        isHideAwaitingStock,
        installationMessages,
        isAwaitingStock,
        specialOffers,
        hasExDisplayMessage,
        productMessages,
      }}
    >
      {children}
    </ProductContext.Provider>
  );
};
